import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BackgroundBlur from "../components/login/ui/BackgroundBlur";
import { authService } from "../services/authService";
import { KeyRound, CheckCircle, AlertCircle, Eye, EyeOff, ArrowLeft } from "lucide-react";

const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-[#1d57a5] focus:border-[#1d57a5] outline-none transition-all duration-200";

export default function ChangePassword() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ 
    currentPassword: "",
    newPassword: "", 
    confirmPassword: "" 
  });
  const [show, setShow] = useState({ 
    newPassword: false, 
    confirmPassword: false 
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    setPasswordStrength(strength);
  };

  const getStrengthColor = () => {
    if (passwordStrength === 0) return "bg-gray-200 dark:bg-gray-700";
    if (passwordStrength === 1) return "bg-red-500";
    if (passwordStrength === 2) return "bg-orange-500";
    if (passwordStrength === 3) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthText = () => {
    if (passwordStrength === 0) return "No password";
    if (passwordStrength === 1) return "Weak";
    if (passwordStrength === 2) return "Fair";
    if (passwordStrength === 3) return "Good";
    return "Strong";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      
      if (!userId) {
        throw new Error("User not authenticated. Please login again.");
      }

      const payload = {
        id: parseInt(userId),
        newPassword: form.newPassword,
        confirmPassword: form.confirmPassword
      };

      const response = await authService.changepassword(payload);
      if (response) {
        setSuccess(true);
        setTimeout(() => navigate(-1), 2000);
      }   
    } catch (err) {
      if (err.errors) {
        const errorMessages = Object.values(err.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Failed to change password. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-full w-full bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 overflow-hidden">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 relative">
        <BackgroundBlur />
        
        <div className="w-full max-w-md relative z-10">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-0.5 transition-transform" />
            <span className="text-sm font-medium">Back</span>
          </button>

          {/* Header */}
          <div className="mb-8">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1d57a5] to-[#0f3061] flex items-center justify-center mb-5 shadow-lg">
              <KeyRound size={28} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
              Credentials
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Update your password to maintain account security
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white/80 dark:bg-slate-800/60 backdrop-blur-xl rounded-2xl shadow-xl border border-slate-200/50 dark:border-slate-700/50 p-6 sm:p-8">
            {success ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={40} className="text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
                  Password Updated!
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Your password has been changed successfully.
                </p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-3">
                  Redirecting you back...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Error Alert */}
                {error && (
                  <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl px-4 py-3">
                    <AlertCircle size={18} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
                  </div>
                )}

                {/* Current Password Field */}
                <div>
                  
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={show.currentPassword ? "text" : "password"}
                      value={form.currentPassword}
                      onChange={(e) => {
                        setForm(p => ({ ...p, currentPassword: e.target.value }));
                        checkPasswordStrength(e.target.value);
                      }}
                      placeholder="Enter new password"
                      required
                      className={`${inputCls} pr-11`}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShow(p => ({ ...p, currentPassword: !p.currentPassword }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      disabled={isLoading}
                    >
                      {show.currentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  
                  {/* Password Strength Indicator */}
                  {/* {form.currentPassword.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-1 h-1.5">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className={`flex-1 rounded-full transition-all duration-300 ${
                              passwordStrength >= level
                                ? getStrengthColor()
                                : "bg-gray-200 dark:bg-gray-700"
                            }`}
                          />
                        ))}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          Password strength:
                        </span>
                        <span className={`text-xs font-medium ${
                          passwordStrength === 1 ? "text-red-500" :
                          passwordStrength === 2 ? "text-orange-500" :
                          passwordStrength === 3 ? "text-yellow-500" :
                          passwordStrength === 4 ? "text-green-500" :
                          "text-slate-400"
                        }`}>
                          {getStrengthText()}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400 dark:text-slate-500 space-y-1">
                        <p className={form.currentPassword.length >= 8 ? "text-green-500" : ""}>
                          • At least 8 characters
                        </p>
                        <p className={/[A-Z]/.test(form.currentPassword) && /[a-z]/.test(form.currentPassword) ? "text-green-500" : ""}>
                          • Uppercase and lowercase letters
                        </p>
                        <p className={/[0-9]/.test(form.currentPassword) ? "text-green-500" : ""}>
                          • At least one number
                        </p>
                        <p className={/[^a-zA-Z0-9]/.test(form.currentPassword) ? "text-green-500" : ""}>
                          • At least one special character
                        </p>
                      </div>
                    </div>
                  )} */}
                </div>

                {/* New Password Field */}
                <div>

                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={show.newPassword ? "text" : "password"}
                      value={form.newPassword}
                      onChange={(e) => {
                        setForm(p => ({ ...p, newPassword: e.target.value }));
                        checkPasswordStrength(e.target.value);
                      }}
                      placeholder="Enter new password"
                      required
                      className={`${inputCls} pr-11`}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShow(p => ({ ...p, newPassword: !p.newPassword }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      disabled={isLoading}
                    >
                      {show.newPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  
                  {/* Password Strength Indicator */}
                  {form.newPassword.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-1 h-1.5">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className={`flex-1 rounded-full transition-all duration-300 ${
                              passwordStrength >= level
                                ? getStrengthColor()
                                : "bg-gray-200 dark:bg-gray-700"
                            }`}
                          />
                        ))}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          Password strength:
                        </span>
                        <span className={`text-xs font-medium ${
                          passwordStrength === 1 ? "text-red-500" :
                          passwordStrength === 2 ? "text-orange-500" :
                          passwordStrength === 3 ? "text-yellow-500" :
                          passwordStrength === 4 ? "text-green-500" :
                          "text-slate-400"
                        }`}>
                          {getStrengthText()}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400 dark:text-slate-500 space-y-1">
                        <p className={form.newPassword.length >= 8 ? "text-green-500" : ""}>
                          • At least 8 characters
                        </p>
                        <p className={/[A-Z]/.test(form.newPassword) && /[a-z]/.test(form.newPassword) ? "text-green-500" : ""}>
                          • Uppercase and lowercase letters
                        </p>
                        <p className={/[0-9]/.test(form.newPassword) ? "text-green-500" : ""}>
                          • At least one number
                        </p>
                        <p className={/[^a-zA-Z0-9]/.test(form.newPassword) ? "text-green-500" : ""}>
                          • At least one special character
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={show.confirmPassword ? "text" : "password"}
                      value={form.confirmPassword}
                      onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))}
                      placeholder="Re-enter new password"
                      required
                      className={`${inputCls} pr-11`}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShow(p => ({ ...p, confirmPassword: !p.confirmPassword }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      disabled={isLoading}
                    >
                      {show.confirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  
                  {/* Match Indicator */}
                  {form.confirmPassword.length > 0 && (
                    <div className="mt-2">
                      {form.newPassword === form.confirmPassword ? (
                        <p className="text-xs text-green-500 flex items-center gap-1">
                          <CheckCircle size={12} /> Passwords match
                        </p>
                      ) : (
                        <p className="text-xs text-red-500">Passwords do not match</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || (form.newPassword !== form.confirmPassword)}
                  className="w-full py-3.5 bg-gradient-to-r from-[#1d57a5] to-[#164280] hover:from-[#164280] hover:to-[#0f3061] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mt-6"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <KeyRound size={18} />
                      <span>Update Password</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel - Hero Section */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#1d57a5] via-[#1a4f96] to-[#0f3061] items-center justify-center relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#36a852]/20 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#ffd966]/10 rounded-full blur-3xl animate-pulse delay-500" />
        </div>
        
        <div className="relative z-10 text-center px-12 max-w-md">
          <div className="w-24 h-24 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center mx-auto mb-8 animate-float">
            <KeyRound size={48} className="text-white" />
          </div>
          <h3 className="text-3xl font-bold text-white mb-4">Secure Your Account</h3>
          <p className="text-white/70 text-base leading-relaxed">
            Create a strong password to protect your account from unauthorized access. 
            A strong password includes a mix of letters, numbers, and special characters.
          </p>
          
          {/* Security Tips */}
          <div className="mt-8 p-5 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 text-left">
            <p className="text-white/90 text-sm font-semibold mb-3">Security Tips:</p>
            <ul className="space-y-2 text-white/60 text-xs">
              <li className="flex items-center gap-2">• Use at least 12 characters for maximum security</li>
              <li className="flex items-center gap-2">• Avoid using personal information like your name or birthdate</li>
              <li className="flex items-center gap-2">• Don't reuse passwords across different accounts</li>
              <li className="flex items-center gap-2">• Consider using a password manager</li>
            </ul>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}